
import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PhoneInput } from "@/components/ui/phone-input";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { countries } from "@/lib/countries";

const translations = {
  en: {
    title: "Student Application Form",
    fullName: "Full Name",
    email: "Email Address",
    phone: "Phone Number",
    nationality: "Nationality",
    programLevel: "Program Level",
    intendedCourse: "Intended Course / Major",
    studyDestination: "Preferred Study Destination",
    hasEnglishTest: "Do you have an English proficiency test?",
    additionalNotes: "Additional Notes / Questions",
    submit: "Submit Application",
    success: "Application submitted successfully!",
    error: "Error submitting application. Please try again."
  },
  ar: {
    title: "نموذج طلب الطالب",
    fullName: "الاسم الكامل",
    email: "البريد الإلكتروني",
    phone: "رقم الهاتف",
    nationality: "الجنسية",
    programLevel: "مستوى البرنامج",
    intendedCourse: "التخصص المطلوب",
    studyDestination: "وجهة الدراسة المفضلة",
    hasEnglishTest: "هل لديك اختبار كفاءة في اللغة الإنجليزية؟",
    additionalNotes: "ملاحظات إضافية / أسئلة",
    submit: "تقديم الطلب",
    success: "تم تقديم الطلب بنجاح!",
    error: "خطأ في تقديم الطلب. يرجى المحاولة مرة أخرى."
  },
  so: {
    title: "Foomka Codsiga Ardayga",
    fullName: "Magaca Oo Buuxa",
    email: "Ciwaanka Emailka",
    phone: "Lambarka Telefoonka",
    nationality: "Jinsiyadda",
    programLevel: "Heerka Barnaamijka",
    intendedCourse: "Qaybta La Rabo",
    studyDestination: "Goobta Waxbarashada Ee La Rabo",
    hasEnglishTest: "Ma haysataa imtixaanka aqoonta Ingiriisiga?",
    additionalNotes: "Faahfaahin Dheeraad ah / Su'aalo",
    submit: "Gudbi Codsiga",
    success: "Codsiga si guul leh ayaa loo gudbiyey!",
    error: "Khalad ayaa ka dhacay gudbinta codsiga. Fadlan isku day mar kale."
  }
};

const programLevels = [
  { value: "foundation", label: "Foundation" },
  { value: "bachelor", label: "Bachelor" },
  { value: "master", label: "Master" },
  { value: "phd", label: "PhD" }
];

export function ApplicationForm({ language = "en", open, onOpenChange }) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    nationality: "",
    programLevel: "",
    intendedCourse: "",
    studyDestination: "",
    hasEnglishTest: "no",
    additionalNotes: ""
  });

  const content = translations[language] || translations.en;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const [firstName, ...lastNameParts] = formData.fullName.split(' ');
      const lastName = lastNameParts.join(' ');

      const { data, error } = await supabase
        .from('applications')
        .insert([{
          first_name: firstName,
          last_name: lastName || '',
          email: formData.email,
          phone_number: parseInt(formData.phone.replace(/\D/g, '')),
          nationality: formData.nationality,
          course_level: formData.programLevel,
          subject: formData.intendedCourse,
          study_country: formData.studyDestination,
          english_test_name: formData.hasEnglishTest,
          summary: formData.additionalNotes
        }])
        .select();

      if (error) throw error;

      // Send email notification
      await fetch('https://api.resend.com/emails', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_RESEND_API_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          from: 'The Horn Educational Network <info@thehorneducation.com>',
          to: 'info@thehorneducation.com',
          subject: '📝 New Student Application',
          html: `
            <h2>New Student Application</h2>
            <p><strong>Name:</strong> ${formData.fullName}</p>
            <p><strong>Email:</strong> ${formData.email}</p>
            <p><strong>Phone:</strong> ${formData.phone}</p>
            <p><strong>Nationality:</strong> ${formData.nationality}</p>
            <p><strong>Program Level:</strong> ${formData.programLevel}</p>
            <p><strong>Intended Course:</strong> ${formData.intendedCourse}</p>
            <p><strong>Study Destination:</strong> ${formData.studyDestination}</p>
            <p><strong>English Test:</strong> ${formData.hasEnglishTest}</p>
            <p><strong>Additional Notes:</strong> ${formData.additionalNotes}</p>
          `
        })
      });

      toast({
        title: content.success,
        duration: 3000
      });

      onOpenChange(false);
      setFormData({
        fullName: "",
        email: "",
        phone: "",
        nationality: "",
        programLevel: "",
        intendedCourse: "",
        studyDestination: "",
        hasEnglishTest: "no",
        additionalNotes: ""
      });
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: content.error,
        variant: "destructive",
        duration: 3000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{content.title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="grid gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                {content.fullName}
              </label>
              <input
                required
                type="text"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                className="w-full p-2 rounded-md border"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                {content.email}
              </label>
              <input
                required
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full p-2 rounded-md border"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                {content.phone}
              </label>
              <PhoneInput
                value={formData.phone}
                onChange={(value) => setFormData({ ...formData, phone: value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                {content.nationality}
              </label>
              <select
                required
                value={formData.nationality}
                onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                className="w-full p-2 rounded-md border"
              >
                <option value="">Select nationality</option>
                {countries.map((country) => (
                  <option key={country.code} value={country.name}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                {content.programLevel}
              </label>
              <select
                required
                value={formData.programLevel}
                onChange={(e) => setFormData({ ...formData, programLevel: e.target.value })}
                className="w-full p-2 rounded-md border"
              >
                <option value="">Select program level</option>
                {programLevels.map((level) => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                {content.intendedCourse}
              </label>
              <input
                required
                type="text"
                value={formData.intendedCourse}
                onChange={(e) => setFormData({ ...formData, intendedCourse: e.target.value })}
                className="w-full p-2 rounded-md border"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                {content.studyDestination}
              </label>
              <select
                required
                value={formData.studyDestination}
                onChange={(e) => setFormData({ ...formData, studyDestination: e.target.value })}
                className="w-full p-2 rounded-md border"
              >
                <option value="">Select destination</option>
                <option value="UK">United Kingdom</option>
                <option value="Malaysia">Malaysia</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                {content.hasEnglishTest}
              </label>
              <select
                required
                value={formData.hasEnglishTest}
                onChange={(e) => setFormData({ ...formData, hasEnglishTest: e.target.value })}
                className="w-full p-2 rounded-md border"
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                {content.additionalNotes}
              </label>
              <textarea
                rows={4}
                value={formData.additionalNotes}
                onChange={(e) => setFormData({ ...formData, additionalNotes: e.target.value })}
                className="w-full p-2 rounded-md border"
              />
            </div>
          </div>

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {content.submit}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
